import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createApiClient} from "../api/client";
import {
    getQoqById,
    getReportById,
    listInstitutions,
    listQoqPeriods,
    listQoqPositionsBySnapshot,
    listQoqPositionsByTickerRange,
} from "../api/endpoints";
import type {InstitutionalHoldingReportDto, QoqPositionsPage} from "../api/dtos";
import {PositionData, QuarterData} from "../components/13FQoqHistory/types";
import {useAliveRef} from "./useAliveRef";
import {
    appendGroupedByPeriod,
    buildAggregatedTickerPivot,
    buildPivotInstitutionMode,
    buildRowPivot,
    computeAllYearQuarters,
    filterAndMapFactory,
    isValidTicker,
    makePairsTickerMode,
    RowKey,
    sanitizeTicker,
    sortPairsByNewestValueTicker,
    toFromPeriod,
    totalValueAtTicker,
    toToPeriod,
    UIPair,
} from "../helpers/useHoldingsHistoryHelper";

const api = createApiClient();

export type Mode = "institution" | "ticker";

export interface ColumnMeta {
    period: string;
    snapshotId: number;
    baseReportId: number | null;
    report?: Pick<
        InstitutionalHoldingReportDto,
        | "id"
        | "filingUri"
        | "isReportedTableValueTotalSuspect"
        | "isProcessedTableValueTotalSuspect"
        | "processedInfoTableValueUnitEnum"
        | "reportedInfoTableValueUnitEnum"
    >;
}

export interface UseHoldingsHistoryArgs {
    mode: Mode;
    institutionId: number;

    ticker: string;
    tickerQueried: boolean;

    fromYear: number;
    toYear: number;
    fromYearQuarter?: string;
    toYearQuarter?: string;

    showPositionTypes: Record<string, boolean>;
    enabled?: boolean;
}

export interface UseHoldingsHistoryState {
    loading: boolean;
    pageError: string | null;
    tickerError: string | null;

    data: QuarterData[];
    institutionMap: Record<number, string>;
    allYearQuarters: string[];
    newestYQWithData: string | null;

    columnMetaByPeriod: Record<string, ColumnMeta>;

    pivotInstitution?: Record<string, Record<string, Record<string, PositionData>>>;
    pivotTicker?: Record<RowKey, Record<string, PositionData>>;
    pairsTicker?: UIPair[];
    sortedPairsTicker?: UIPair[];
    pivotTickerAgg?: Record<RowKey, Record<string, PositionData>>;
    pairsTickerAgg?: UIPair[];
    sortedPairsTickerAgg?: UIPair[];

    totalValueInNewestQuarter: number;

    canLoadMore: boolean;
    loadingMore: boolean;
    loadMore: () => void;
}

/* page sizes */
const INITIAL_LIMIT_PER_PERIOD = 100;
const LOAD_MORE_PAGE_SIZE = 100;
const TICKER_INITIAL_LIMIT = 1000;
const TICKER_FILLER_LIMIT = 500;

/* helpers */
const pairKey = (p: PositionData) => `${(p.ticker || "").trim()}|${String(p.positionType)}`;
const pairsOf = (items: PositionData[]) => {
    const s = new Set<string>();
    for (const p of items) {
        const t = (p.ticker || "").trim();
        if (!t) continue;
        s.add(pairKey(p));
    }
    return s;
};
const tickersFromPairs = (pairs: Iterable<string>) => Array.from(new Set(Array.from(pairs).map((k) => k.split("|")[0])));

type CacheEntry = {
    data: QuarterData[];
    periodCursors: Record<number, string | null>;
    periodsOrder: { period: string; snapshotId: number }[];
    tickerCursor: string | null;
    columnMetaByPeriod: Record<string, ColumnMeta>;
};

export function useHoldingsHistory(args: UseHoldingsHistoryArgs): UseHoldingsHistoryState {
    const {
        mode,
        institutionId,
        ticker,
        tickerQueried,
        fromYear,
        toYear,
        fromYearQuarter,
        toYearQuarter,
        enabled = true,
    } = args;

    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [tickerError, setTickerError] = useState<string | null>(null);

    const [institutionMap, setInstitutionMap] = useState<Record<number, string>>({});
    const [data, setData] = useState<QuarterData[]>([]);
    const [periodCursors, setPeriodCursors] = useState<Record<number, string | null>>({});
    const [tickerCursor, setTickerCursor] = useState<string | null>(null);
    const [periodsOrder, setPeriodsOrder] = useState<{ period: string; snapshotId: number }[]>([]);
    const [columnMetaByPeriod, setColumnMetaByPeriod] = useState<Record<string, ColumnMeta>>({});
    const [loadingMore, setLoadingMore] = useState(false);

    const aliveRef = useAliveRef();
    const anchorPairsRef = useRef<Set<string>>(new Set());

    // caching
    const lastQueryRef = useRef<string | null>(null);
    const inflightKeyRef = useRef<string | null>(null);
    const cacheRef = useRef<Record<string, CacheEntry>>({});

    // live mirrors
    const dataRef = useRef<QuarterData[]>([]);
    const cursorsRef = useRef<Record<number, string | null>>({});
    const orderRef = useRef<{ period: string; snapshotId: number }[]>([]);
    const tickerCursorRef = useRef<string | null>(null);

    const columnMetaRef = useRef<Record<string, ColumnMeta>>({});

    useEffect(() => {
        columnMetaRef.current = columnMetaByPeriod;
    }, [columnMetaByPeriod]);

    useEffect(() => void (dataRef.current = data), [data]);
    useEffect(() => void (cursorsRef.current = periodCursors), [periodCursors]);
    useEffect(() => void (orderRef.current = periodsOrder), [periodsOrder]);
    useEffect(() => void (tickerCursorRef.current = tickerCursor), [tickerCursor]);

    const fromPeriod = fromYearQuarter ?? toFromPeriod(fromYear);
    const toPeriod = toYearQuarter ?? toToPeriod(toYear);

    const mapNormalize = useMemo(() => filterAndMapFactory({SHARE: true, CALL: true, PUT: true}), []);

    const appendItemsToPeriod = useCallback((period: string, items: PositionData[]) => {
        if (!items?.length) return;
        setData((prev) => {
            const idx = prev.findIndex((q) => q.period === period);
            if (idx === -1) return prev;
            const target = prev[idx];
            const nextForPeriod = {...target, positions: [...target.positions, ...items]};
            const next = prev.slice();
            next[idx] = nextForPeriod;
            return next;
        });
    }, []);

    const hydrateMissingForPeriod = useCallback(
        async (snapshotId: number, period: string, tickers: Iterable<string>, neededPairs?: Set<string>) => {
            const list = Array.from(new Set(Array.from(tickers))).filter(Boolean);
            if (!list.length) return [] as PositionData[];

            const resp: QoqPositionsPage =
                await listQoqPositionsBySnapshot(api, snapshotId, {
                    tickers: list,
                    limit: TICKER_FILLER_LIMIT,
                    sort: "VALUE_DESC",
                }).catch(
                    () =>
                        ({
                            items: [],
                            page: {nextCursor: null, limit: TICKER_FILLER_LIMIT},
                        } as QoqPositionsPage)
                );

            if (!aliveRef.current) return [] as PositionData[];

            let mapped = mapNormalize(resp.items);
            if (neededPairs && neededPairs.size) {
                mapped = mapped.filter((p) => neededPairs.has(pairKey(p)));
            }
            if (mapped.length) appendItemsToPeriod(period, mapped);
            return mapped;
        },
        [appendItemsToPeriod, mapNormalize, aliveRef]
    );

    // Prefetch institution names exactly once (ticker mode)
    useEffect(() => {
        if (mode !== "ticker" || Object.keys(institutionMap).length > 0) return;
        let cancelled = false;
        (async () => {
            try {
                const insts = await listInstitutions(api).catch(() => []);
                if (cancelled || !aliveRef.current) return;
                const map: Record<number, string> = {};
                (insts ?? []).forEach((i: any) => {
                    if (i?.id) map[i.id] = i.name ?? `Inst ${i.id}`;
                });
                setInstitutionMap(map);
            } catch {
                /* noop */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mode, institutionMap, aliveRef]);

    // Main loader
    useEffect(() => {
        const queryKey =
            mode === "ticker"
                ? `ticker|${sanitizeTicker(ticker)}|${fromPeriod}|${toPeriod}`
                : `inst|${institutionId}|${fromPeriod}|${toPeriod}`;

        if (!enabled) {
            setLoading(false);
            return;
        }

        // save old query to cache on switch
        const prevKey = lastQueryRef.current;
        if (typeof prevKey === "string" && prevKey !== queryKey && dataRef.current.length > 0) {
            cacheRef.current[prevKey] = {
                data: dataRef.current,
                periodCursors: cursorsRef.current,
                periodsOrder: orderRef.current,
                tickerCursor: tickerCursorRef.current ?? null,
                columnMetaByPeriod: columnMetaRef.current,
            };
        }

        // restore from cache if available
        const cached = cacheRef.current[queryKey];
        if (cached) {
            setData(cached.data);
            setPeriodCursors(cached.periodCursors);
            setPeriodsOrder(cached.periodsOrder);
            setTickerCursor(cached.tickerCursor ?? null);
            setColumnMetaByPeriod(cached.columnMetaByPeriod ?? {});
            setLoading(false);
            lastQueryRef.current = queryKey;
            return;
        }

        // prevent duplicate in-flight runs (this was causing qoq?from=.. spam)
        if (inflightKeyRef.current === queryKey) return;

        const run = async () => {
            setLoading(true);
            setPageError(null);
            setTickerError(null);

            // mark as in-flight immediately
            inflightKeyRef.current = queryKey;
            lastQueryRef.current = queryKey; // lock re-entrancy right away

            // reset state
            setData([]);
            setPeriodCursors({});
            setPeriodsOrder([]);
            setTickerCursor(null);
            setColumnMetaByPeriod({});
            anchorPairsRef.current = new Set();

            // INSTITUTION MODE
            if (mode === "institution") {
                if (!Number.isFinite(institutionId) || institutionId <= 0) {
                    setLoading(false);
                    inflightKeyRef.current = null;
                    return;
                }

                // 1) Available periods (newest -> oldest)
                const periods = await listQoqPeriods(api, institutionId, fromPeriod, toPeriod).catch((e: any) => {
                    setPageError(String(e?.message || "Failed to list periods"));
                    return [] as any[];
                });
                if (!aliveRef.current) return;

                const ordered = [...(periods ?? [])]
                    .sort((a: any, b: any) => (a.period < b.period ? 1 : -1))
                    .map((p: any) => ({period: p.period, snapshotId: p.activeSnapshotId}));

                const accData: QuarterData[] = ordered.map((p) => ({
                    year: Number(p.period.slice(0, 4)),
                    quarter: `Q${p.period.slice(5)}`,
                    period: p.period,
                    positions: [],
                }));
                const indexByPeriod = new Map<string, number>(ordered.map((p, i) => [p.period, i]));
                const addLocal = (period: string, items: PositionData[]) => {
                    if (!items?.length) return;
                    const idx = indexByPeriod.get(period);
                    if (idx === undefined) return;
                    accData[idx].positions.push(...items);
                };

                // push empty shells to paint headers immediately
                setData(accData);
                setPeriodsOrder(ordered);

                // also pull column metadata (snapshot -> base report -> suspect flags)
                const metaList = await Promise.all(
                    ordered.map(async (o) => {
                        try {
                            const snap = await getQoqById(api, o.snapshotId);
                            const baseId = snap?.baseReportId ?? null;

                            let report: ColumnMeta["report"] | undefined;
                            if (baseId != null) {
                                const rep = await getReportById(api, baseId).catch(() => null);
                                if (rep) {
                                    report = {
                                        id: rep.id,
                                        filingUri: rep.filingUri,
                                        isReportedTableValueTotalSuspect: rep.isReportedTableValueTotalSuspect,
                                        isProcessedTableValueTotalSuspect: rep.isProcessedTableValueTotalSuspect,
                                        processedInfoTableValueUnitEnum: rep.processedInfoTableValueUnitEnum,
                                        reportedInfoTableValueUnitEnum: rep.reportedInfoTableValueUnitEnum,
                                    };
                                }
                            }
                            return {
                                period: o.period,
                                snapshotId: o.snapshotId,
                                baseReportId: baseId,
                                report
                            } as ColumnMeta;
                        } catch {
                            return {period: o.period, snapshotId: o.snapshotId, baseReportId: null} as ColumnMeta;
                        }
                    })
                );
                const metaMap: Record<string, ColumnMeta> = {};
                metaList.forEach((m) => (metaMap[m.period] = m));
                setColumnMetaByPeriod(metaMap);

                if (ordered.length === 0) {
                    setLoading(false);
                    cacheRef.current[queryKey] = {
                        data: [],
                        periodCursors: {},
                        periodsOrder: [],
                        tickerCursor: null,
                        columnMetaByPeriod: {},
                    };
                    inflightKeyRef.current = null;
                    return;
                }

                // 2) Fetch newest quarter first
                const head = ordered[0];
                const headResp: QoqPositionsPage =
                    await listQoqPositionsBySnapshot(api, head.snapshotId, {
                        limit: INITIAL_LIMIT_PER_PERIOD,
                        sort: "VALUE_DESC",
                    }).catch(
                        () =>
                            ({
                                items: [],
                                page: {limit: INITIAL_LIMIT_PER_PERIOD, nextCursor: null},
                            } as QoqPositionsPage)
                    );

                if (!aliveRef.current) return;

                const headMapped = mapNormalize(headResp.items);
                setData((prev) => appendGroupedByPeriod(prev, headMapped));
                addLocal(head.period, headMapped);

                const cursors: Record<number, string | null> = {
                    [head.snapshotId]: headResp.page?.nextCursor ?? null,
                };

                anchorPairsRef.current = pairsOf(headMapped);

                // 3) Preload older quarters in parallel
                await Promise.all(
                    ordered.slice(1).map(async (item) => {
                        const resp: QoqPositionsPage =
                            await listQoqPositionsBySnapshot(api, item.snapshotId, {
                                limit: INITIAL_LIMIT_PER_PERIOD,
                                sort: "VALUE_DESC",
                            }).catch(
                                () =>
                                    ({
                                        items: [],
                                        page: {limit: INITIAL_LIMIT_PER_PERIOD, nextCursor: null},
                                    } as QoqPositionsPage)
                            );

                        if (!aliveRef.current) return;

                        const mapped = mapNormalize(resp.items);
                        setData((prev) => appendGroupedByPeriod(prev, mapped));
                        addLocal(item.period, mapped);
                        cursors[item.snapshotId] = resp.page?.nextCursor ?? null;

                        const periodPairs = pairsOf(mapped);
                        const missingPairs = new Set([...anchorPairsRef.current].filter((k) => !periodPairs.has(k)));
                        if (missingPairs.size) {
                            const missingTickers = tickersFromPairs(missingPairs);
                            const filled = await hydrateMissingForPeriod(item.snapshotId, item.period, missingTickers, missingPairs);
                            if (filled?.length) addLocal(item.period, filled);
                        }
                    })
                );

                if (aliveRef.current) {
                    setPeriodCursors(cursors);
                    // prime cache
                    cacheRef.current[queryKey] = {
                        data: accData,
                        periodCursors: cursors,
                        periodsOrder: ordered,
                        tickerCursor: null,
                        columnMetaByPeriod: metaMap,
                    };
                    setLoading(false);
                    inflightKeyRef.current = null;
                }
                return;
            }

            // TICKER MODE
            if (!tickerQueried || !ticker.trim()) {
                setLoading(false);
                cacheRef.current[queryKey] = {
                    data: [],
                    periodCursors: {},
                    periodsOrder: [],
                    tickerCursor: null,
                    columnMetaByPeriod: {},
                };
                inflightKeyRef.current = null;
                return;
            }

            const tkr = sanitizeTicker(ticker);
            if (!isValidTicker(tkr)) {
                setTickerError("Invalid ticker format. Use letters, numbers, '.' or '-'.");
                setLoading(false);
                cacheRef.current[queryKey] = {
                    data: [],
                    periodCursors: {},
                    periodsOrder: [],
                    tickerCursor: null,
                    columnMetaByPeriod: {},
                };
                inflightKeyRef.current = null;
                return;
            }

            let resp: QoqPositionsPage | undefined;
            try {
                resp = await listQoqPositionsByTickerRange(api, tkr, {
                    from: fromPeriod,
                    to: toPeriod,
                    limit: TICKER_INITIAL_LIMIT,
                    sort: "VALUE_DESC",
                });
            } catch (err: any) {
                const status = err?.response?.status;
                if (status === 400) {
                    const msg = err?.response?.data?.message || err?.response?.data?.reason || "Bad request.";
                    setTickerError(msg);
                } else if (status === 404) {
                    setTickerError(`Ticker "${tkr}" was not found.`);
                } else {
                    setTickerError("Something went wrong while fetching. Please try again.");
                }
                setLoading(false);
                cacheRef.current[queryKey] = {
                    data: [],
                    periodCursors: {},
                    periodsOrder: [],
                    tickerCursor: null,
                    columnMetaByPeriod: {},
                };
                inflightKeyRef.current = null;
                return;
            }

            if (!aliveRef.current) return;

            const raw = Array.isArray(resp?.items) ? resp!.items : [];
            const onlyThisTicker = raw.filter((r) => !!r?.period).filter((r) => (r.ticker || "").toUpperCase() === tkr);
            const mapped = mapNormalize(onlyThisTicker);

            const grouped = appendGroupedByPeriod([], mapped);
            setData(grouped);

            const nextCur = resp?.page?.nextCursor ?? null;
            setTickerCursor(nextCur);

            cacheRef.current[queryKey] = {
                data: grouped,
                periodCursors: {},
                periodsOrder: [],
                tickerCursor: nextCur,
                columnMetaByPeriod: {},
            };

            setLoading(false);
            inflightKeyRef.current = null;
        };

        void run();
    }, [
        mode,
        institutionId,
        ticker,
        tickerQueried,
        fromPeriod,
        toPeriod,
        enabled,
        aliveRef,
        mapNormalize,
        hydrateMissingForPeriod,
    ]);

    // infinite scroll
    const canLoadMore = useMemo(() => {
        if (mode === "institution") return periodsOrder.some((p) => periodCursors[p.snapshotId]);
        if (mode === "ticker") return tickerQueried && !!tickerCursor;
        return false;
    }, [mode, periodsOrder, periodCursors, tickerCursor, tickerQueried]);

    const loadMore = useCallback(async () => {
        if (loadingMore || !canLoadMore) return;

        if (mode === "institution") {
            setLoadingMore(true);
            try {
                const toPage = periodsOrder.filter((p) => periodCursors[p.snapshotId]);
                if (!toPage.length) return;

                const prevAnchor = new Set(anchorPairsRef.current);

                const round = await Promise.all(
                    toPage.map(async (item) => {
                        const cursor = periodCursors[item.snapshotId]!;
                        const resp: QoqPositionsPage =
                            await listQoqPositionsBySnapshot(api, item.snapshotId, {
                                limit: LOAD_MORE_PAGE_SIZE,
                                sort: "VALUE_DESC",
                                cursor,
                            }).catch(
                                () =>
                                    ({
                                        items: [],
                                        page: {limit: LOAD_MORE_PAGE_SIZE, nextCursor: null},
                                    } as QoqPositionsPage)
                            );

                        if (!aliveRef.current) return null;

                        const mapped = mapNormalize(resp.items);
                        appendItemsToPeriod(item.period, mapped);

                        return {item, resp, mapped, periodPairs: pairsOf(mapped)};
                    })
                );

                if (!aliveRef.current) return;

                setPeriodCursors((prev) => {
                    const next = {...prev};
                    for (const r of round) {
                        if (!r) continue;
                        next[r.item.snapshotId] = r.resp.page?.nextCursor ?? null;
                    }
                    return next;
                });

                const headId = periodsOrder[0]?.snapshotId;
                const headRound = round.find((r) => r && r.item.snapshotId === headId);

                let addedPairs = new Set<string>();
                if (headRound) {
                    for (const k of headRound.periodPairs) anchorPairsRef.current.add(k);
                    addedPairs = new Set([...headRound.periodPairs].filter((k) => !prevAnchor.has(k)));
                }

                if (addedPairs.size) {
                    const getPairsForPeriod = (period: string) => {
                        const q = dataRef.current.find((d) => d.period === period);
                        return q ? pairsOf(q.positions) : new Set<string>();
                    };

                    await Promise.all(
                        periodsOrder.slice(1).map(async (p) => {
                            const have = getPairsForPeriod(p.period);
                            const missing = new Set([...addedPairs].filter((k) => !have.has(k)));
                            if (!missing.size) return;

                            const tickers = Array.from(new Set(Array.from(missing).map((k) => k.split("|")[0])));
                            await hydrateMissingForPeriod(p.snapshotId, p.period, tickers, missing);
                        })
                    );
                }
            } finally {
                setLoadingMore(false);
            }
            return;
        }

        if (mode === "ticker") {
            const cur = tickerCursorRef.current;
            if (!cur) return;
            setLoadingMore(true);
            try {
                const tkr = sanitizeTicker(ticker);
                const resp: QoqPositionsPage =
                    await listQoqPositionsByTickerRange(api, tkr, {
                        from: fromPeriod,
                        to: toPeriod,
                        limit: LOAD_MORE_PAGE_SIZE,
                        cursor: cur,
                        sort: "VALUE_DESC",
                    }).catch(
                        () =>
                            ({
                                items: [],
                                page: {limit: LOAD_MORE_PAGE_SIZE, nextCursor: null},
                            } as QoqPositionsPage)
                    );

                if (!aliveRef.current) return;

                const raw = Array.isArray(resp?.items) ? resp.items : [];
                const onlyThisTicker = raw.filter((r) => !!r?.period).filter((r) => (r.ticker || "").toUpperCase() === tkr);
                const mapped = mapNormalize(onlyThisTicker);

                setData((prev) => appendGroupedByPeriod(prev, mapped));
                setTickerCursor(resp.page?.nextCursor ?? null);
            } finally {
                setLoadingMore(false);
            }
        }
    }, [
        loadingMore,
        canLoadMore,
        mode,
        periodsOrder,
        periodCursors,
        appendItemsToPeriod,
        hydrateMissingForPeriod,
        mapNormalize,
        aliveRef,
        ticker,
        fromPeriod,
        toPeriod,
    ]);

    // derived UI state
    const allYearQuarters = useMemo(() => computeAllYearQuarters(data, fromYear, toYear), [data, fromYear, toYear]);
    const newestYQWithData = allYearQuarters[0] ?? null;

    const pivotInstitution = useMemo(
        () => (mode === "institution" ? buildPivotInstitutionMode(data) : undefined),
        [mode, data]
    );
    const pivotTicker = useMemo(() => (mode === "ticker" ? buildRowPivot(data) : undefined), [mode, data]);

    const {aggPivot: pivotTickerAgg, aggPairs: pairsTickerAgg} = useMemo(() => {
        if (mode !== "ticker" || !pivotTicker) return {aggPivot: undefined, aggPairs: undefined};
        return buildAggregatedTickerPivot(pivotTicker);
    }, [mode, pivotTicker]);

    const pairsTicker = useMemo(() => (pivotTicker ? makePairsTickerMode(pivotTicker) : undefined), [pivotTicker]);
    const sortedPairsTicker = useMemo(
        () => (pairsTicker && pivotTicker ? sortPairsByNewestValueTicker(pairsTicker, pivotTicker, newestYQWithData) : undefined),
        [pairsTicker, pivotTicker, newestYQWithData]
    );
    const sortedPairsTickerAgg = useMemo(
        () => (pairsTickerAgg && pivotTickerAgg ? sortPairsByNewestValueTicker(pairsTickerAgg, pivotTickerAgg, newestYQWithData) : undefined),
        [pairsTickerAgg, pivotTickerAgg, newestYQWithData]
    );

    const totalValueInNewestQuarter = useMemo(() => {
        if (!newestYQWithData) return 0;
        if (pairsTicker && pivotTicker) return totalValueAtTicker(pairsTicker, pivotTicker, newestYQWithData);
        return 0;
    }, [pairsTicker, pivotTicker, newestYQWithData]);

    return {
        loading,
        pageError,
        tickerError,
        data,
        institutionMap,
        allYearQuarters,
        newestYQWithData,
        columnMetaByPeriod,

        pivotInstitution,

        pivotTicker,
        pairsTicker,
        sortedPairsTicker,

        pivotTickerAgg,
        pairsTickerAgg,
        sortedPairsTickerAgg,

        totalValueInNewestQuarter,

        canLoadMore,
        loadingMore,
        loadMore,
    };
}
